import { lazy } from 'react'
import {
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  HomeModernIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import type { VerticalModule } from '@lipaflow/shared'
import { RENTAL_DEFAULT_CONTEXT } from './context/defaultContext'
import RentalInviteScopeFields from './components/RentalInviteScopeFields'
import { rentalHomeActions, rentalNeedsYou } from './lib/needsYou'

const OwnersPage = lazy(() => import('./pages/OwnersPage'))
const BuildingsPage = lazy(() => import('./pages/BuildingsPage'))
const BuildingFundPage = lazy(() => import('./pages/BuildingFundPage'))
const TenantsPage = lazy(() => import('./pages/TenantsPage'))
const UnitsPage = lazy(() => import('./pages/UnitsPage'))
const MaintenancePage = lazy(() => import('./pages/MaintenancePage'))
const RentRollPage = lazy(() => import('./pages/RentRollPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const RentReceivedPage = lazy(() => import('./pages/RentReceivedPage'))
const PayOwnersPage = lazy(() => import('./pages/PayOwnersPage'))
const MessagesPage = lazy(() => import('./pages/MessagesPage'))
const MessageSettingsPanel = lazy(() => import('./components/MessageSettingsPanel'))
const SetupChecklist = lazy(() => import('./components/SetupChecklist'))
const PortfolioImports = lazy(() => import('./components/PortfolioImport'))
const HomeMonth = lazy(() => import('./components/HomeMonth'))
const RentRulesPanel = lazy(() => import('./components/RentRulesPanel'))
const TenancyTermsPage = lazy(() => import('./pages/TenancyTermsPage'))
const ImportTenantsPage = lazy(() => import('./pages/ImportTenantsPage'))

const PROPERTY_MANAGEMENT = 'property_management'
const AGENCY = 'agency_managed'

/**
 * The rental vertical as an *operator* shell composes it — a managing agent's
 * lease book and maintenance board. Separate from the payer `rentalModule`
 * for the same reason education's are: one vertical, two audiences, and route
 * paths that would otherwise collide.
 */
export const rentalOperatorModule: VerticalModule = {
  code: 'rental',
  defaultContext: RENTAL_DEFAULT_CONTEXT,
  entityIcon: BuildingOfficeIcon,

  // The managing agent's day: the building stock first, then the money that
  // flows out to owners. Units already live under Properties, so the shared
  // entity list is renamed to what it actually shows -- balances per unit.
  navItems: [
    // The one list of units; each opens as its record, which holds the lease (design 2026-10-12, D1).
    { to: '/units', label: 'Units', icon: HomeModernIcon, group: 'workspace', section: 'Properties', requiresPermission: 'units:view', keywords: ['units', 'tenants', 'leases', 'vacant', 'arrears', 'balances', 'move in', 'notice', 'move out', 'renew', 'deposits', 'statement'] },
    // Who an agency manages for: each owner's record holds their units and money (statement, expenses, payouts).
    { to: '/owners', label: 'Owners', icon: UserGroupIcon, group: 'workspace', section: 'Properties', requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'owners:view', operatingModels: [AGENCY], keywords: ['owners', 'landlords', 'payouts', 'statements', 'expenses', 'commission', 'kra pin', 'payout account', 'remittance'] },
    // A landlord's few buildings sit in a tab beside Units instead (D9).
    { to: '/buildings', label: 'Buildings', icon: BuildingOfficeIcon, group: 'workspace', section: 'Properties', requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'properties:view', operatingModels: [AGENCY], keywords: ['properties', 'buildings', 'address', 'location', 'staff'] },
    { to: '/maintenance', label: 'Repairs & maintenance', icon: WrenchScrewdriverIcon, group: 'workspace', section: 'Properties', requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'maintenance:view', keywords: ['repairs', 'requests', 'tickets'] },
    // Money (2026-10-14): Approvals (shared), then what came in, then each owner's share.
    { to: '/payments', label: 'Rent received', icon: BanknotesIcon, group: 'finance', requiresPermission: 'payments:view', keywords: ['payments', 'mpesa', 'receipts', 'not matched', 'unmatched', 'deposits to assign', 'reverse', 'collections'] },
    { to: '/pay-owners', label: 'Pay owners', icon: CalendarDaysIcon, group: 'finance', requiresPermission: 'payouts:prepare', operatingModels: [AGENCY], keywords: ['month end', 'payouts', 'payout run', 'remittance', 'owner statements', 'transfers', 'bill rent'] },
    // What LipaFlow sent to tenants and owners, and notices (2026-10-14).
    { to: '/messages', label: 'Messages', icon: ChatBubbleLeftRightIcon, group: 'engagement', requiresPermission: 'messages:view', keywords: ['messages', 'sms', 'notices', 'reminders', 'announcements', 'failed'] },
    // People who rent, current and former: the tenant record holds every tenancy.
    { to: '/tenants', label: 'Tenants', icon: UsersIcon, group: 'people', requiresPermission: 'beneficiaries:view', keywords: ['tenants', 'renters', 'contacts', 'former tenants', 'phone'] },
  ],

  sharedNavLabels: {},
  labels: {
    entity: 'Unit',
    payer: 'Tenant',
    group: 'Tenant',
    obligation: 'Charge',
    period: 'Month',
    entity_reference: 'Unit number',
    institution: 'Organisation',
    institution_detail: 'Organisation details',
  },
  operatorLogin: {
    portalName: 'Property Portal',
    tagline: 'Properties, leases, rent and tenant requests in one connected workspace.',
    points: [
      { title: 'Every unit, every payment', description: 'Rent lands in your own account and is matched to the unit.' },
      { title: 'Give tenants a clear view', description: 'Tenants check charges, pay by M-PESA and access receipts in their own portal.' },
      { title: 'Make month end easier', description: 'Rent roll, arrears and owner statements, ready to share.' },
    ],
    emailExample: 'you@yourlettings.co.ke',
  },
  // The grade-shaped balances list is replaced by the unit roster.
  // Tenants replaces the shared contact list: a unit's account has no named payer.
  // Rent received does payments and unmatched money; plans, promises and
  // waivers are started from a unit or tenant and decided in Approvals.
  // Messages (2026-10-14) replaces the WhatsApp-first screens until an inbound channel exists.
  hiddenSharedNav: ['/entities', '/clients', '/payments-received', '/deposits-to-assign', '/payment-plans', '/pledges', '/waivers',
    '/escalations', '/conversations', '/broadcasts', '/templates', '/insights', '/segments'],
  ownsPayerMessages: true,
  sharedPageFor: {
    '/payments-received': '/payments',
    '/deposits-to-assign': '/payments?view=not_matched',
  },

  settingsTabs: [
    { id: 'rent-rules', label: 'Rent rules', slot: 'settingsRentRulesPanel' },
    { id: 'messages', label: 'Messages', slot: 'settingsMessagesPanel' },
  ],

  // Rent a landlord never receives must be visible to them, and a tenant who
  // paid for a repair has paid rent in work: the landlord is credited the rent
  // and charged the repair (decided 2026-09-28).
  waiverReasons: {
    in_kind: {
      label: 'Tenant paid for a repair',
      hint: 'The landlord is credited the rent and charged the repair on their statement.',
      evidenceHint: "The contractor's receipt or invoice, and a photo of the work if you have one.",
      detailsLabel: 'What was repaired, by whom, and what it cost',
    },
    concession: {
      label: 'Rent concession',
      hint: 'An agreed reduction, such as a rent-free month.',
      evidenceHint: "The landlord's written agreement: a signed note, email or message.",
      consentPrompt: "Only if the owner can't use the owner portal: they agreed in writing, and it's attached. Otherwise the owner is sent a link and agrees there.",
    },
  },
  // Rent is billed from the lease and deposits from move-in, so neither is a
  // one-off charge here.
  chargeCategories: [
    { code: 'water', label: 'Water' },
    { code: 'electricity', label: 'Electricity' },
    { code: 'service_charge', label: 'Service charge' },
    { code: 'garbage', label: 'Garbage collection' },
    { code: 'parking', label: 'Parking' },
    { code: 'other', label: 'Something else' },
  ],
  // A managing agency's team, not a school's. Only these are offered.
  roleLabels: {
    admin: 'Admin',
    property_manager: 'Property manager',
    leasing_agent: 'Leasing agent',
    caretaker: 'Caretaker',
    rental_accountant: 'Rental accountant',
    payout_approver: 'Owner payout approver',
    portfolio_viewer: 'Portfolio viewer',
  },

  // Home's one "Needs you" list: units owing, vacant, leaving, owners who can't be paid.
  needsYou: rentalNeedsYou,
  home: { actions: rentalHomeActions },
  // Search finds units, tenants (current and former), owners and buildings.
  recordSearch: { path: '/rental/search' },

  // Shared settings page renders this without knowing what a penalty is.
  slots: {
    homeMonth: HomeMonth,
    setupChecklist: SetupChecklist,
    settingsRentRulesPanel: RentRulesPanel,
    settingsMessagesPanel: MessageSettingsPanel,
    inviteAssignmentFields: RentalInviteScopeFields,
    openingBalancesExtra: PortfolioImports,
  },

  routes: [
    { path: 'units', element: <UnitsPage />, requiresPermission: 'units:view' },
    { path: 'units/:unitId', element: <UnitsPage />, requiresPermission: 'units:view' },
    { path: 'owners', element: <OwnersPage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'owners:view' },
    { path: 'owners/:ownerId', element: <OwnersPage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'owners:view' },
    { path: 'buildings', element: <BuildingsPage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'properties:view' },
    { path: 'buildings/:buildingId', element: <BuildingsPage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'properties:view' },
    // A building's fund (rule 8.4): seen by whoever sees the building's reports.
    { path: 'buildings/:buildingId/fund', element: <BuildingFundPage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'reports:view' },
    { path: 'tenants', element: <TenantsPage />, requiresPermission: 'beneficiaries:view' },
    { path: 'tenants/:clientId', element: <TenantsPage />, requiresPermission: 'beneficiaries:view' },
    // Rules 4.6, 4.11: bulk import of tenants already living here (organisation-wide lease grant).
    { path: 'tenants/import', element: <ImportTenantsPage />, requiresPermission: 'leases:manage' },
    { path: 'maintenance', element: <MaintenancePage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'maintenance:view' },
    { path: 'maintenance/:requestId', element: <MaintenancePage />, requiresFeature: PROPERTY_MANAGEMENT, requiresPermission: 'maintenance:view' },
    { path: 'rent-roll', element: <RentRollPage />, requiresPermission: 'rent_roll:generate' },
    // Rental's own Reports at the shared address (2026-10-16): figures that link to where you act.
    { path: 'reports', element: <ReportsPage />, requiresPermission: 'reports:view' },
    { path: 'payments', element: <RentReceivedPage />, requiresPermission: 'payments:view' },
    { path: 'payments/:paymentId', element: <RentReceivedPage />, requiresPermission: 'payments:view' },
    { path: 'pay-owners', element: <PayOwnersPage />, requiresPermission: 'payouts:prepare' },
    { path: 'messages', element: <MessagesPage />, requiresPermission: 'messages:view' },
    // Rules 4.4-4.8: tenancies whose terms aren't agreed yet; Home's "Needs you" opens it.
    { path: 'tenancy-terms', element: <TenancyTermsPage />, requiresPermission: 'leases:view' },
  ],
}
